// Pending Decisions — blocked by ruling, not unbuilt.
//
// §3.2 of the build instruction opens with DO NOT BUILD. The endpoints this
// surface was originally mapped to (`/api/executive/queue`,
// `/api/recommendations/*`) are live and populated, and that is exactly what
// makes them dangerous: they serve campaign and offer decisions, which §3.6
// excludes here. Wiring them would fill the page with the content the spec
// keeps out, and it would look like it worked.
//
// So this reads nothing. It states the ruling, names the correct source and
// its emptiness, and shows what a decision has to carry when the surface does
// open — labelled a specimen, because it is a contract and not a row.
//
// Replacing the generic "Not built yet" card with this is the whole change:
// the same non-functionality, said properly.
import React from "react";

import PageHead from "../../components/PageHead";
import "./pending-decisions.css";

/** The evidence contract a decision must satisfy. Not data — the shape of it. */
const CONTRACT = [
  ["SUBJECT", "One of nine closed types, with a ref that resolves"],
  ["EVIDENCE REFS", "Every id minted by this run, never inherited"],
  ["MEASUREMENTS", "Typed values, or an explicit absent-reason"],
  ["PROSE CITATIONS", "Each claim in the text traced to a ref"],
  ["DOCTRINE VERSION", "Which rules were in force when it was made"],
  ["INTEGRITY DIGEST", "Answer hash, so tampering is detectable"],
];

const CANDIDATES = [
  "Tool requests",
  "Escalations",
  "Chief of Staff send-backs",
];

export default function PendingDecisions() {
  return (
    <>
      <PageHead
        title="Pending Decisions"
        subtitle="What Cipher is waiting on you to decide."
      />

      <section className="pd-gate" aria-labelledby="pd-h">
        <span className="pd-seal" aria-hidden="true">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none"
               stroke="currentColor" strokeWidth="1.6">
            <rect x="4.5" y="10.5" width="15" height="10" rx="2.5" />
            <path d="M8 10.5V7.5a4 4 0 0 1 8 0v3" />
          </svg>
        </span>

        <h2 id="pd-h">This surface is blocked, not unbuilt</h2>

        <p className="pd-lede">
          §3.2 of the build instruction opens with <b>DO NOT BUILD</b>. The endpoints this
          page was mapped to — <code>/api/executive/queue</code> and{" "}
          <code>/api/recommendations/*</code> — are live and populated, and that is what
          makes them dangerous: they serve <b>campaign and offer decisions</b>, which §3.6
          excludes from this surface. Wiring them would fill the page with the content the
          spec keeps out, and it would look like it worked.
        </p>
        <p className="pd-lede">
          The correct source is{" "}
          <code className="pd-violet">/api/organization/tool-requests</code>, which is
          empty. Which decisions belong here at all is an unsettled Founder ruling.
        </p>

        <h3 className="pd-sec">What a decision must carry, when this opens</h3>
        <ul className="pd-contract">
          {CONTRACT.map(([k, v]) => (
            <li key={k}>
              <span className="pd-k">{k}</span>
              <span className="pd-v">{v}</span>
            </li>
          ))}
        </ul>
        <span className="pd-spec">SPECIMEN · NOT LIVE DATA</span>
      </section>

      <div className="pd-cols">
        <section className="card pd-card">
          <h3>Correct source</h3>
          <div className="pd-sub">WHERE THIS SURFACE WILL READ FROM</div>
          <div className="pd-row">
            <span><i className="pd-dot vi" />/api/organization/tool-requests</span>
            <span className="pd-val">0 ROWS</span>
          </div>
          <p className="pd-note">
            An empty correct source is a better foundation than a full wrong one.
          </p>
        </section>

        <section className="card pd-card">
          <h3>What unblocks it</h3>
          <div className="pd-sub">ONE FOUNDER RULING</div>
          <div className="pd-row">
            <span><i className="pd-dot vi" />Which decisions belong here</span>
            <span className="pd-val">PENDING</span>
          </div>
          {CANDIDATES.map((label) => (
            <div className="pd-row" key={label}>
              <span><i className="pd-dot off" />{label}</span>
              <span className="pd-val">CANDIDATE</span>
            </div>
          ))}
        </section>
      </div>
    </>
  );
}
